import { useLiveQuery } from 'dexie-react-hooks';

/**
 * Result shape required from queries used with `useCachedLiveQuery`. The `pid`
 * tag lets the cache distinguish "fresh data keyed to the current
 * participantId" from "stale data held over from a previous mount with a
 * different participantId". Pass `pid: null` for queries that are not
 * participant-scoped (e.g. user info, feature flags, course metadata).
 */
export type CachedQueryEntry<T> = { pid: string | null; data: T };

const cache = new Map<string, CachedQueryEntry<unknown>>();

/**
 * `useLiveQuery` returns `undefined` on the first synchronous render of every
 * fresh mount because `dexie-react-hooks` has no in-memory cache — it always
 * waits for the observable to emit (one microtask, but enough to flash a
 * loader). On back-navigation, the Dexie data is already warm, but every page
 * remount still pays this round-trip.
 *
 * `useCachedLiveQuery` adds a module-level cache layer above `useLiveQuery`:
 * the last-known `{pid, data}` for each `cacheKey` is held across mounts. On a
 * warm-cache mount it returns the cached data **synchronously on first
 * render**, while the underlying `useLiveQuery` re-subscribes in the
 * background and overwrites the cache when fresher data arrives.
 *
 * The `pid` tag is checked on read: if the cached entry's pid does not match
 * the current pid, the cache miss falls through to `undefined` (loading) so
 * participant A's data is never shown to participant B.
 *
 * @param cacheKey  Stable identifier for this query slot (e.g. `'training-level:courses:<pid>'`).
 * @param pid       Current participant id, or `null` for global queries. Must match the `pid` field returned by `queryFn`.
 * @param queryFn   Same shape as `useLiveQuery`'s querier, but must wrap its result as `{ pid, data }`.
 * @param deps      `useLiveQuery` dep array; re-subscribes on change.
 * @returns         The cached `data` (warm mount, matching pid), the live-query `data` once it emits, or `undefined` while loading.
 */
export function useCachedLiveQuery<T>(
  cacheKey: string,
  pid: string | null,
  queryFn: () => Promise<CachedQueryEntry<T>> | CachedQueryEntry<T>,
  deps: unknown[],
): T | undefined {
  const liveResult = useLiveQuery<CachedQueryEntry<T>>(queryFn, deps);

  // `pid` is `string | null` (never `undefined`), so `liveResult?.pid === pid`
  // is true only when `liveResult` is defined AND its pid matches.
  if (liveResult?.pid === pid) {
    cache.set(cacheKey, liveResult);
    return liveResult.data;
  }

  const cached = cache.get(cacheKey) as CachedQueryEntry<T> | undefined;
  if (cached?.pid === pid) {
    return cached.data;
  }

  return undefined;
}

/**
 * Clears all cached entries. Call from logout, profile switch, and Jest
 * `beforeEach` to keep module-level state from leaking between tests.
 */
export function resetCachedLiveQueryCache(): void {
  cache.clear();
}
